/**
 * @file アクションフローMixin
 * @namespace mixins
 */

import dolly from '@/rahmen/utils/dolly'
import modals from '@/ddv2.library/modals'
import listMixin from '@/mixins/listMixins'
import { mapGetters } from 'vuex'

/**
 * @module flowMixins
 */
/**
 * @vue-data {Object} [cloneData={}] - クローン済みデータ
 * @vue-data {Enum} transitionType - 遷移タイプ
 */
export default {
  mixins: [listMixin],
  data: () => ({
    /**
     * クローンデータ
     */
    cloneData: {},
    /**
     * 遷移タイプ
     * @enum
     */
    transitionType: {
      /**
       * 前方遷移
       * @type Number
       */
      forwarding: 0,
      /**
       * 後方遷移
       * @type Number
       */
      returning: 9
    }
  }),
  /**
   * 画面離脱遷移前イベント
   * @event
   * @param {Vue} to 遷移先View
   * @param {Vue} from 遷移元View
   * @param {Function} next 遷移処理用Callback
   */
  beforeRouteLeave (to, from, next) {
    console.log('beforeRouteLeave')
    this.Effects.loadStart()
    this.beforeRouteLeaveByMenu(next)
  },
  /**
   * 同一画面パラメータ変更時イベント
   * @event
   * @param {Vue} to 遷移先View
   * @param {Vue} from 遷移元View
   * @param {Function} next 遷移処理用Callback
   */
  beforeRouteUpdate (to, from, next) {
    next()
  },
  computed: {
    ...mapGetters({
      currentKinoId: 'system/currentKinoId',
      currentKengenGroupCd: 'users/kengenGroupCd'
    })
  },
  watch: {
    $route: function () {
      console.log('flowMixins.watch.$route', window.location.href)
      // ログオフ時はなにもしない
      if (window.location.href.indexOf('about:blank') > 0) return
      // 別画面遷移時は初期表示処理を行う
      this.initialize()
    }
  },
  methods: {
    /**
     * Mixin用（初期化処理）
     */
    initialize: function () { },
    /**
     * Mixin用（メニューから他画面への遷移）
     */
    beforeRouteLeaveByMenu: function (next) { next() },
    /**
     * Mixin用 (クローン対象値を取得する)
     * @example
     * methods: {
     *   getCloneTargetMethod: function () {
     *     return {
     *       records: this.record,
     *       searchText: this.searchText
     *     }
     *   }
     * }
     */
    getCloneTargetMethod: function () { return {} },
    /**
     * 登録系初期化処理
     * @public
     * @param {Object} option 動作引数
     * @param {Array} option.loadMasters ロード対象マスタ名配列
     * @param {Promise} option.initializeInputPromise 入力項目初期化Promise
     * @param {Promise} option.savedInputRestorePromise 保存済み入力項目復元Promise
     * @param {Function} option.existsIdMethod モード判定ID取得メソッド
     * @param {Promise<String>} option.requestApiPromise APIアクセスPromise
     * @param {Promise} option.settingButtonStatePromise ボタン状態設定Promise
     * @param {Function} option.settingItemMethod 表示項目設定メソッド
     * @param {Promise} option.settingLoadEffect ローディングエフェクト設定
     */
    initializeForRegister: function (option) {
      const self = this
      const opt = {
        loadMasters: option.loadMasters || [],
        initializeInputPromise: option.initializeInputPromise || this._dummyPromiseMethod,
        savedInputRestorePromise: option.savedInputRestorePromise || this._dummyPromiseMethod,
        existsIdMethod: option.existsIdMethod || this._dummyGetIdMethod,
        requestApiPromise: option.requestApiPromise || this._dummyPromiseMethod,
        settingButtonStatePromise: option.settingButtonStatePromise || this._dummyPromiseMethod,
        settingItemMethod: option.settingItemMethod || this._dummyPromiseMethod,
        settingLoadEffect: option.settingLoadEffect || this._dummyBoolMethod
      }

      console.log('initializeForRegister', opt)

      self.setEnableViewScroll(true)

      if (opt.settingLoadEffect() === false) {
        // CLローディングエフェクトON
        self.Effects.loadStart()
      }

      // 画面タイトル取得
      self._setViewTitlePromise()

      // SummaryMessage初期化
      self.clearSummaryMessage()

      // InvalidMessage初期化
      self.clearInvalidMessage(self.$refs)

      self.$store.dispatch('DDMasters/restoreMasters')

      self.$store.dispatch('DDMasters/loadMasters', { list: [...opt.loadMasters], version: 1 }).then(() => {
        // 入力内容初期化（デリゲート
        opt.initializeInputPromise().then(() => {
          console.log('flowMixins.initializeForRegister.initializeInputPromise')
          // ユーザ毎設定値取得
          self._savedInputRestorePromise(opt.savedInputRestorePromise)

          // ID判定
          const requestPromise = opt.existsIdMethod() ? opt.requestApiPromise : self._dummyPromiseMethod
          console.log('requestPromise', requestPromise)

          if (opt.settingLoadEffect() === false) {
            // CLローディングエフェクトOFF
            self.Effects.loadComplete()
            // APIローディングエフェクトON
            self.Effects.loadStart('loading2')
          }

          // APIリクエスト＆値取得
          requestPromise().then(() => {
            console.log('flowMixins.initializeForRegister.requestPromise')
            // 権限取得
            const kengenParam = {
              kinoId: self.currentKinoId,
              groupId: self.currentKengenGroupCd
            }
            self.$store.dispatch('DDMasters/getKengen', kengenParam).then((kengen) => {
              // ボタン設定
              opt.settingButtonStatePromise(kengen).then(() => {
                // 表示モードによる項目設定
                opt.settingItemMethod(kengen)
                // 初期値クローン
                self.cloneData = dolly.clone(self.getCloneTargetMethod())

                if (opt.settingLoadEffect() === false) {
                  // APIローディングエフェクトOFF
                  self.Effects.loadComplete('loading2')
                }
              })
            })
          })
        })
      })
    },
    /**
     * 検索系初期化処理
     * @param {Object} option 動作引数
     * @param {Array} option.loadMasters ロード対象マスタ名配列
     * @param {Promise} option.defineHeaderPromise 一覧ヘッダ設定Promise
     * @param {Promise} option.initializeInputPromise 画面初期化Promise
     * @param {Promise} option.savedInputRestorePromise 保存済み入力項目復元Promise
     * @param {Promise} option.researchPromise 戻り時再検索Promise(検索動作制御区分='2')
     * @param {Promise} option.restoreViewPromise 戻り時復元Promise(検索動作制御区分='3')
     * @param {Promise} option.settingButtonStatePromise ボタン状態設定Promise
     * @param {Promise} option.settingViewScroll スクロール設定
     * @param {Promise} option.settingLoadEffect ローディングエフェクト設定
     */
    initializeForViewer: function (option) {
      const self = this
      const opt = {
        loadMasters: option.loadMasters || [],
        defineHeaderPromise: option.defineHeaderPromise || this._dummyPromiseMethod,
        initializeInputPromise: option.initializeInputPromise || this._dummyPromiseMethod,
        savedInputRestorePromise: option.savedInputRestorePromise || this._dummyPromiseMethod,
        researchPromise: option.researchPromise || this._dummyPromiseMethod,
        restoreViewPromise: option.restoreViewPromise || this._dummyPromiseMethod,
        settingButtonStatePromise: option.settingButtonStatePromise || this._dummyPromiseMethod,
        settingViewScroll: option.settingViewScroll || this._dummyTrueMethod,
        settingLoadEffect: option.settingLoadEffect || this._dummyBoolMethod
      }

      console.log('initializeForViewer', opt)

      self.setEnableViewScroll(opt.settingViewScroll())

      console.log('flowMixins.setEnableViewScroll')

      if (opt.settingLoadEffect() === false) {
        // ローディングエフェクトON
        self.Effects.loadStart()
      }

      console.log('flowMixins.loadStart')

      // 画面タイトル取得
      self._setViewTitlePromise()

      console.log('flowMixins._setViewTitlePromise')

      // SummaryMessage初期化
      self.clearSummaryMessage()

      // InvalidMessage初期化
      self.clearInvalidMessage(self.$refs)

      // マスタ取得
      self.$store.dispatch('DDMasters/loadMasters', { list: [...opt.loadMasters], version: 1 }).then(() => {
        const initPromises = [opt.defineHeaderPromise(), opt.initializeInputPromise()]
        Promise.all(initPromises).then(() => {
          console.log('flowMixins.Promise.all')

          // 遷移タイプ判定
          const forward = self._determineTransitionType() !== self.transitionType.returning
          console.log('flowMixins._determineTransitionType', forward)

          // 初期化メイン処理
          const mainPromise = (forward)
            ? self._savedInputRestorePromise(opt.savedInputRestorePromise)
            : self._returningSearchPromise(opt)

          mainPromise.then((p) => {
            console.log('flowMixins.mainPromise', self.currentKengenGroupCd)
            const kengenParam = {
              kinoId: self.currentKinoId,
              groupId: self.currentKengenGroupCd
            }
            console.log(mainPromise)
            if (![undefined, null].includes(p) && p.returnSearchMode) {
              self.page = p.cache.value ? p.cache.value.page ? p.cache.value.page : self.page : self.page
              self.itemsPerPage = p.cache.value ? p.cache.value.itemsPerPage ? p.cache.value.itemsPerPage : self.itemsPerPage : self.itemsPerPage
              self.searchText = p.cache.value ? p.cache.value.searchText ? p.cache.value.searchText : self.searchText : self.searchText
            }
            self.$store.dispatch('DDMasters/getKengen', kengenParam).then((kengen) => {
              // ボタン設定
              opt.settingButtonStatePromise(kengen).then(() => {
                console.log('flowMixins.settingButtonStatePromise')
                // 初期値クローン
                self.cloneData = dolly.clone(self.getCloneTargetMethod())

                if (opt.settingLoadEffect() === false) {
                  // ローディングエフェクトOFF
                  self.Effects.loadComplete()
                }
              })
            })
          })
        })
      })
    },
    /**
     * クリア
     * @param {Object} option 動作引数
     * @param {Function} option.getCloneTargetMethod クローンデータ取得メソッド
     * @param {Promise} option.clearInputsPromise 入力項目初期化Promise
     */
    clearInputs: function (option) {
      const self = this
      const opt = {
        clearInputsPromise: option.clearInputsPromise || self._dummyPromiseMethod
      }

      const target = self.getCloneTargetMethod()
      const modified = self._compareClone(target)

      const p = modified ? modals.showConfirmYesNo : self._dummyPromiseMethod
      p('COMQ0006').then((ok) => {
        console.log('clearInputs')
        if (ok === false || ok === undefined) { return }
        self.clearInvalidMessage(self.$refs)
        // SummaryMessage初期化
        self.clearSummaryMessage()
        const p = opt.clearInputsPromise()
        console.log('clearInputs', p)
      })
      self.Effects.loadComplete()
    },
    /**
     * 戻る
     * @param {Object} option 動作引数
     * @param {Function} option.getTransitionBackParams 戻り画面遷移用パラメータ取得Function
     * @example
     * onReturnButtonClick: function (e) {
      console.log('onReturnButtonClick', e)
      const self = this
      const opt = {
        getTransitionBackParams: function () {
          return {
            actionId: 0,
            param: { id: self.id }
          }
        }
      }

      this.transitionBack(opt)
    }
     */
    transitionBack: function (option) {
      if (option === undefined || option.getTransitionBackParams === undefined) {
        return
      }

      const p = option.getTransitionBackParams()
      if (p.param === undefined) { p.param = {} }
      p.param.transitionType = this.transitionType.returning
      this.transition(p.actionId, p.param)
    },
    /**
     * キャンセル
     * @param {Object} option 動作引数
     * @param {Promise} option.restoreInputsPromise 画面初期値設定Promise
     */
    cancelInputs: function (option) {
      const self = this
      const opt = {
        restoreInputsPromise: option.restoreInputsPromise || self._dummyPromiseMethod
      }

      const target = self.getCloneTargetMethod()
      const modified = self._compareClone(target)

      const p = modified ? modals.showConfirmYesNo : self._dummyPromiseMethod
      p('COMQ0007').then((ok) => {
        if (ok === false || ok === undefined) { return }
        // SummaryMessage初期化
        self.clearSummaryMessage()
        self.clearInvalidMessage(self.$refs)
        const data = dolly.clone(self.cloneData)
        opt.restoreInputsPromise(() => data)
        self.cloneData = dolly.clone(self.getCloneTargetMethod())
      })
    },
    /**
     * 編集中の他画面への遷移ガード
     * @param {Object} option 動作引数
     * @param {Function} option.getCloneTargetMethod クローンデータ取得メソッド
     * @param {Function} option.nextCallback 画面遷移処理
     */
    preventTransition: function (option) {
      const self = this
      const opt = {
        nextCallback: option.nextCallback
      }

      // ログオフ時は強制的遷移
      if (window.location.href.indexOf('about:blank') > 0) opt.nextCallback()

      // 変更チェックの為ローディングエフェクトOFF
      this.Effects.loadComplete()

      // 別画面遷移の場合は変更チェックを行う
      const target = self.getCloneTargetMethod()
      const modified = self._compareClone(target)
      const p = modified ? modals.showConfirmYesNo : self._dummyPromiseMethod
      p('COMQ0005').then((ok) => {
        if (modified && (ok === false || ok === undefined)) {
          opt.nextCallback(false)
          return
        }

        // ローディングエフェクトON
        self.Effects.loadStart()
        opt.nextCallback()
      })
    },
    /**
     * 複写
     * @param {Object} option 動作引数
     * @param {Function} option.setIdMethod ID設定Method
     */
    copyInputs: function (option) {
      const self = this
      const opt = {
        setIdMethod: option.setIdMethod || self._dummyVoidMethod
      }
      const p = modals.showConfirmYesNo
      p('COMQ0004').then((ok) => {
        console.log('copyInputs')
        if (ok === false || ok === undefined) { return }
        // SummaryMessage初期化
        self.clearSummaryMessage()
        self.clearInvalidMessage(self.$refs)
        opt.setIdMethod()
      })
    },
    /**
     * 検索処理
     * @param {Object} option 動作引数
     * @param {Promise} option.searchRequestPromise 検索処理Promise
     * @param {Function} option.searchResultMethod 検索結果設定メソッド
     * @param {Function} option.getSavedInputMethod 保存済み入力値マスタ値返却メソッド
     */
    search: function (option) {
      const self = this
      const opt = {
        searchRequestPromise: option.searchRequestPromise || self._dummyPromiseMethod,
        searchResultMethod: option.searchResultMethod || self._dummyObjectsMethod,
        getSavedInputMethod: option.getSavedInputMethod || self._dummyObjectsMethod,
        isNoMessageModeMethod: option.isNoMessageModeMethod || self._dummyBoolMethod
      }

      const isValid = self._validateSearchProcess()
      if (!isValid) { return }

      const isNoMessage = opt.isNoMessageModeMethod()

      // CLロードエフェクトON
      self.Effects.loadStart()

      // SummaryMessage初期化
      self.clearSummaryMessage()

      const param = {
        kinoId: self.currentKinoId,
        obj: opt.getSavedInputMethod()
      }

      // CLロードエフェクトOFF
      self.Effects.loadComplete()

      // APIローディングエフェクトON
      self.Effects.loadStart('loading2')

      self.$store.dispatch('DDMasters/registerSavedInput', param).then(() => {
        opt.searchRequestPromise().then(data => {
          console.log('searchRequestPromise', data)

          self._setMessages(data).then(() => {
            if (data.isValid) {
              opt.searchResultMethod(data)
              if (!isNoMessage) {
                if (data.payload && data.payload.length > 0) {
                  modals.showToast('検索しました', null, 400, null, 'info')
                  data.payload = null
                } else {
                  modals.showToast('対象データがありませんでした', null, 450, null, 'info')
                }
              }
            }
            // APIローディングエフェクトOFF
            self.Effects.loadComplete('loading2')
          })
        })
      })
    },
    /**
     * 削除処理
     * @param {Object} option 動作引数
     * @param {Promise} option.deleteRequestPromise 削除処理Promise
     * @param {Function} option.getTransitionBackParams 戻り画面遷移用パラメータ取得Function
     * @param {Function} option.getSavedInputMethod 保存済み入力値マスタ値返却メソッド
     * @param {Function} option.settingReloadWindow 削除後、登録画面をリロードするか否か
     * @param {Function} option.failureResponsePromise 登録失敗時処理Promise
     * @param {Function} option.isInvokeCallbackOnSuccess 削除後、任意のCallbackを実行するか(settingReloadWindowより先に判定)
     * @param {Function} option.onSuccessCallback 削除後、isInvokeCallbackOnSuccess()==trueの場合に実行するコールバック関数
     */
    delete: function (option) {
      const self = this
      const opt = {
        deleteRequestPromise: option.deleteRequestPromise || self._dummyPromiseMethod,
        getTransitionBackParams: option.getTransitionBackParams || self._dummyObjectsMethod,
        getSavedInputMethod: option.getSavedInputMethod || self._dummyObjectsMethod,
        settingReloadWindow: option.settingReloadWindow || self._dummyBoolMethod,
        failureResponsePromise: option.failureResponsePromise || self._dummyPromiseMethod,
        isInvokeCallbackOnSuccess: option.isInvokeCallbackOnSuccess || self._dummyBoolMethod,
        onSuccessCallback: option.onSuccessCallback || self._dummyObjectsMethod
      }

      const isValid = self._validateDeleteProcess()
      if (!isValid) { return }

      const p = modals.showConfirmYesNo
      p('COMQ0003').then((ok) => {
        if (ok === false || ok === undefined) { return }

        self.Effects.loadStart()

        // SummaryMessage初期化
        self.clearSummaryMessage()

        const param = {
          kinoId: self.currentKinoId,
          obj: opt.getSavedInputMethod()
        }

        self.$store.dispatch('DDMasters/registerSavedInput', param).then(() => {
          opt.deleteRequestPromise().then(data => {
            console.log('deleteRequestPromise', data)
            const p = data.isValid ? modals.showAlert('COMI0003') : self._setMessages(data)
            p.then(() => {
              if (data.isValid) {
                // 初期値クローン
                self.cloneData = dolly.clone(self.getCloneTargetMethod())

                if (opt.isInvokeCallbackOnSuccess()) {
                  opt.onSuccessCallback().then(c => {
                    self.Effects.loadComplete()
                  })
                } else if (opt.settingReloadWindow() === false) {
                  self.transitionBack({ getTransitionBackParams: opt.getTransitionBackParams })
                } else {
                  self.$router.go({ path: self.$router.currentRoute.path, force: true })
                }
              } else {
                opt.failureResponsePromise().then(c => {
                  self.Effects.loadComplete()
                })
              }
            })
          })
        })
      })
    },
    /**
     * メッセージ表示処理
     * @param {Object} param0
     * @private
     */
    _setMessages: function ({ summaryMessage, invalidList }) {
      const self = this
      return new Promise(resolve => {
        if (summaryMessage !== null) {
          console.log('_setMessage', summaryMessage)
          // self.setSummaryMessage(summaryMessage)
          var type = 'info'
          if (['C', 'E'].includes(summaryMessage.messageKbn)) {
            type = 'error'
          }
          modals.showToast(summaryMessage.message, null, 450, null, type)
        }
        self.$nextTick().then(() => {
          self.setInvalidMessage(self.$refs, invalidList)
          resolve()
        })
      })
    },
    /**
     * 登録・更新処理
     * @param {Object} option
     * @param {Promise} option.registerRequestPromise 登録処理Promise
     * @param {Promise} option.registerResponsePromise 登録結果取得Promise
     * @param {Promise} option.continuousInputInitializePromise 登録後連続入力用Promise
     * @param {Function} option.getTransitionBackParams 登録後画面遷移用メソッド
     * @param {Function} option.getIdMethod ID取得用メソッド
     * @param {Function} option.isUpdateModeMethod 更新モード判定メソッド
     * @param {Function} option.getSavedInputMethod 保存済み入力値マスタ値返却メソッド
     * @param {Function} option.judgeKeyChangedMethod キー項目変更判定用メソッド
     * @param {Function} option.failureResponsePromise 登録失敗時処理Promise
     * @example
     * onSaveButtonClick: function (e) {
      const self = this
      const opt = {
        registerRequestPromise: self.registerPromise,
        registerResponsePromise: (data) => {
          return new Promise(resolve => {
            self.id = data.payload.id
            resolve()
          })
        },
        getIdMethod: () => {
          return self.id
        },
        continuousInputInitializePromise: (id) => {
          return new Promise(resolve => {
            this.id = id
            this.transition(1, { id: id }, this.initialize)
          })
        },
        getTransitionBackParams: function () {
          return {
            actionId: 0,
            param: { id: self.id }
          }
        },
        isUpdateModeMethod: function () {
          return self.id !== 0
        },
        judgeKeyChangedMethod: function (data) {
          return data.hacchakuchiCd !== this.rec.hacchakuchiCd
        }
      }

      this.register(opt)
    },
    registerPromise: function () {
      let f
      if (this.id === 0) {
        f = this.HttpClient.post('/api/v1/msthacchakuchi/create', this.rec)
      } else {
        f = this.HttpClient.put(`/api/v1/msthacchakuchi/update?id=${this.id}`, this.rec)
      }
      return f
    }
     */
    register: function (option) {
      const self = this
      const opt = {
        registerRequestPromise: option.registerRequestPromise || self._dummyPromiseMethod,
        registerResponsePromise: option.registerResponsePromise || self._dummyPromiseMethod,
        continuousInputInitializePromise: option.continuousInputInitializePromise || self._dummyPromiseMethod,
        getTransitionBackParams: option.getTransitionBackParams || self._dummyObjectsMethod,
        getIdMethod: option.getIdMethod || self._dummyGetIdMethod,
        isUpdateModeMethod: option.isUpdateModeMethod || function () { return false },
        getSavedInputMethod: option.getSavedInputMethod || self._dummyObjectsMethod,
        isNoRefreshModeMethod: option.isNoRefreshModeMethod || self._dummyBoolMethod,
        isNoMessageModeMethod: option.isNoMessageModeMethod || self._dummyBoolMethod,
        judgeKeyChangedMethod: option.judgeKeyChangedMethod || function () { return false },
        failureResponsePromise: option.failureResponsePromise || self._dummyPromiseMethod,
        validateNotificationModeMethod: option.validateNotificationModeMethod || function () { return false },
        validateNotificationMethod: option.validateNotificationMethod || self._dummyObjectsMethod
      }

      const isValid = self._validateRegisterProcess()
      if (!isValid) {
        if (opt.validateNotificationModeMethod()) {
          opt.validateNotificationMethod()
          return
        } else {
          modals.showToastMessage('COME0001', null, 600, null, null)
          this.controlScrollPosition('.page-top', 0)
          return
        }
      }

      const isUpdate = opt.isUpdateModeMethod()

      const isNoRefresh = opt.isNoRefreshModeMethod()

      const isNoMessage = opt.isNoMessageModeMethod()

      // cloneDataを引数として、キー項目変更判定メソッドを実行
      const isKeyChanged = opt.judgeKeyChangedMethod(self.cloneData)

      // 更新かつキー変更時は、_keyChangedComfirmを、それ以外の場合は、ダミーPromiseを設定する
      const keyChangedConfirm = (isUpdate && isKeyChanged)
        ? this._keyChangedComfirm()
        : new Promise((resolve) => {
          resolve(true)
        })

      keyChangedConfirm.then(ok => {
        // !okの場合は、以降の処理をすべてスキップ
        if (!ok) { return }

        self.Effects.loadStart()

        const param = {
          kinoId: self.currentKinoId,
          obj: opt.getSavedInputMethod()
        }

        self.$store.dispatch('DDMasters/registerSavedInput', param).then(() => {
          opt.registerRequestPromise().then(data => {
            console.log('registerRequestPromise', data)

            // メッセージ非表示の場合はサマリメッセージを削除
            if (isNoMessage === true) {
              if (data.isValid) {
                data.summaryMessage = null
              }
            }

            self._setMessages(data).then(() => {
              if (!data.isValid) {
                opt.failureResponsePromise().then(c => {
                  self.Effects.loadComplete()
                })
                return
              }
              opt.registerResponsePromise(data).then(() => {
                // 初期値クローン
                self.cloneData = dolly.clone(self.getCloneTargetMethod())

                const kinoId = this.currentKinoId

                this.$store.dispatch('DDMasters/getKinoRecord', kinoId).then((k) => {
                  console.log('registerRequestPromise', k)
                  if (k.torokuOperationCtrlKbn === 'x') {
                    console.error('msysKino.torokuOperationCtrlKbnに『x』が設定されています。')
                    return
                  }
                  const id = k.torokuOperationCtrlKbn === '2' ? opt.getIdMethod() : 0
                  if (k.torokuOperationCtrlKbn === '1' || k.torokuOperationCtrlKbn === '2') {
                    if (isUpdate) {
                      // 更新時
                      opt.continuousInputInitializePromise(id).then(() => {
                        if (isNoMessage === false) {
                          const p = data.isValid ? data.summaryMessage.messageCd ? self._dummyPromiseMethod() : modals.showToastMessage('COMI0001') : self._setMessages(data)
                          if (isNoRefresh === false) {
                            p.then(() => {
                              self.$router.go({ path: self.$router.currentRoute.path, force: true })
                            })
                          } else {
                            self.Effects.loadComplete()
                          }
                        } else {
                          self.Effects.loadComplete()
                        }
                      })
                    } else {
                      // 新規時
                      opt.continuousInputInitializePromise(id).then()
                    }
                  } else if (k.torokuOperationCtrlKbn === '3') {
                    self.transitionBack({ getTransitionBackParams: opt.getTransitionBackParams })
                  } else if (k.torokuOperationCtrlKbn === '0') {
                    // 区分:'0' ... 登録状態のままなにもしない（再読み込みもしない）
                    self.Effects.loadComplete()
                  }
                })
              })
            })
          })
        })
      })
    },
    /**
     * 共通：登録時キー変更メッセージ表示
     * @private
     */
    _keyChangedComfirm: function () {
      return new Promise(resolve => {
        modals.showConfirmYesNo('COMQ0008').then((ok) => {
          if (ok === false || ok === undefined) { resolve(false) }
          resolve(true)
        })
      })
    },
    /**
     * 共通：ユーザ毎設定値関係Promise
     * @param {Function} f ユーザ毎設定値取得Function
     * @private
     */
    _savedInputRestorePromise: function (f) {
      return new Promise((resolve) => {
        this.$store.dispatch('DDMasters/getSavedInputs', this.currentKinoId).then((obj) => {
          console.log('flowMixins.DDMasters/getSavedInputs', obj)
          // ユーザ毎設定値設定（デリゲート
          f(obj).then(() => {
            resolve()
          })
        })
      })
    },
    /**
     * 検索画面初期表示時：遷移タイプがreturnの時の処理
     * @private
     */
    _returningSearchPromise: function (opt) {
      console.log('_returningSearchPromise', opt)
      return new Promise((resolve) => {
        const kinoId = this.currentKinoId

        this.$store.dispatch('DDMasters/getKinoRecord', kinoId).then((k) => {
          console.log('_returningSearchPromise', k)
          this.loadFromCache().then((data) => {
            const param = {
              cache: data,
              kensakuOperationCtrlKbn: k.kensakuOperationCtrlKbn,
              torokuOperationCtrlKbn: k.torokuOperationCtrlKbn,
              returnSearchMode: true
            }

            let p = this._dummyPromiseMethod
            if (k.kensakuOperationCtrlKbn === '2') { p = opt.researchPromise }
            if (k.kensakuOperationCtrlKbn === '3') { p = opt.restoreViewPromise }

            // 検索系キャッシュリストア
            p(param).then(() => {
              resolve(param)
            })
          })
        })
      })
    },
    /**
     * 検索検証処理（コンポーネント・個別）
     * @returns {Boolean}
     */
    _validateSearchProcess: function () {
      const self = this
      let isValid = self.validateAll(self.$refs)
      if (isValid) { isValid = self.searchValidate() }
      if (!isValid) { return false }
      return true
    },
    /**
     * 登録更新検証処理（コンポーネント・個別）
     * @returns {Boolean}
     */
    _validateRegisterProcess: function () {
      const self = this
      let isValid = self.validateAll(self.$refs)
      if (isValid) { isValid = self.registerValidate() }
      if (!isValid) { return false }
      return true
    },
    /**
     * 削除検証処理（コンポーネント・個別）
     * @returns {Boolean}
     */
    _validateDeleteProcess: function () {
      const self = this
      let isValid = self.validateAll(self.$refs)
      if (isValid) { isValid = self.deleteValidate() }
      if (!isValid) { return false }
      return true
    },
    /**
     * 画面タイトル設定
     * @private
     */
    _setViewTitlePromise: function () {
      this.$store.dispatch('DDMasters/getCurrentKinoName', this.currentKinoId).then((title) => {
        // 画面タイトル設定
        this.$store.dispatch('system/setViewTitle', title)
      })
    },
    /**
     * クローン値との比較
     * @private
     * @returns {Boolean} true: 変更なし
     */
    _compareClone: function (target) {
      console.log('_compareClone', target, this.cloneData)
      const t = JSON.stringify(target)
      const c = JSON.stringify(this.cloneData)

      return t !== c
    },
    /**
     * ダミープロミス処理
     * @private
     */
    _dummyPromiseMethod: () => {
      return new Promise((resolve) => { resolve() })
    },
    /**
     * ダミーID取得処理
     * @private
     */
    _dummyGetIdMethod: () => {
      return false
    },
    /**
     * ダミーオブジェクト取得処理
     * @private
     */
    _dummyObjectsMethod: () => {
      return {}
    },
    /**
     * 遷移パターン取得処理
     * @private
     */
    _determineTransitionType: function () {
      return this.$route.params.transitionType
    },
    /**
     * ダミー処理
     * @private
     */
    _dummyVoidMethod: () => {
    },
    /**
     * ダミーフラグ処理
     * @private
     */
    _dummyBoolMethod: () => {
      return false
    },
    _dummyTrueMethod: () => {
      return true
    }
  }
}
